import React, { useRef } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, Download, X } from "lucide-react";

export const ACCEPT_FILES = ".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.webp,.gif";

export const downloadAttachment = async (att) => {
  try {
    const res = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = att.filename;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  } catch (e) { toast.error(formatErr(e)); }
};

export const FileLinks = ({ files, testid }) => (files?.length ? (
  <div className="flex flex-wrap gap-1.5 mt-1">
    {files.map((f) => (
      <button key={f.id} type="button" data-testid={`${testid}-file-${f.id}`} onClick={() => downloadAttachment(f)}
        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-sm border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 max-w-[220px]">
        <Download className="w-3 h-3 shrink-0" /><span className="truncate">{f.filename}</span>
      </button>
    ))}
  </div>
) : null);

export const AttachToPayment = ({ obligationId, paymentId, onAdded, testid }) => {
  const ref = useRef(null);
  const upload = async (list) => {
    for (const file of list) {
      const fd = new FormData(); fd.append("file", file); fd.append("payment_id", paymentId);
      try { await api.post(`/obligations/${obligationId}/attachments`, fd); toast.success(`${file.name} attached`); } catch (e) { toast.error(`${file.name}: ${formatErr(e)}`); }
    }
    onAdded();
  };
  return (
    <>
      <input ref={ref} data-testid={`${testid}-input`} type="file" multiple accept={ACCEPT_FILES} className="hidden"
        onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} />
      <button type="button" data-testid={testid} title="Attach receipt / acknowledgement" onClick={() => ref.current?.click()}
        className="p-1 rounded text-slate-400 hover:text-blue-700 hover:bg-blue-50 transition-colors"><Paperclip className="w-4 h-4" /></button>
    </>
  );
};

export const FilePicker = ({ files, onChange, testid }) => {
  const ref = useRef(null);
  return (
    <div data-testid={testid}>
      <input ref={ref} data-testid={`${testid}-input`} type="file" multiple accept={ACCEPT_FILES} className="hidden"
        onChange={(e) => { onChange([...files, ...e.target.files]); e.target.value = ""; }} />
      <button type="button" data-testid={`${testid}-add`} onClick={() => ref.current?.click()}
        className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors">
        <Paperclip className="w-4 h-4" />Attach document
      </button>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} data-testid={`${testid}-item-${i}`} className="flex items-center gap-2 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1">
              <span className="truncate flex-1">{f.name}</span>
              <span className="mono text-slate-400">{f.size < 1024 ? "<1 KB" : `${(f.size / 1024).toFixed(0)} KB`}</span>
              <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => onChange(files.filter((_, j) => j !== i))}><X className="w-3.5 h-3.5 text-slate-500" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
